import { fetcher } from '#lib/backendFetcher';
import { filtersToQueryParams } from '#lib/tableFilters';

/**
 * Fetches aggregated statistics, per-status counts and the blocked-recipient count of a campaign.
 * @param {string} campaignId - UUID of the campaign
 * @returns {Promise<{stats: Object, statusCounts: Object<string, number>, blockedCount: number}>}
 */
export const fetchCampaignStats = async (campaignId) => {
	return fetcher(`/api/v1/email/campaigns/${campaignId}/statistics`);
};

/**
 * Fetches one page of email sends for the table view.
 * @param {string} campaignId - UUID of the campaign
 * @param {Object} [options]
 * @param {{status?: string[], opened?: boolean, clicked?: boolean, unsubscribed?: boolean, search?: string}}
 *   [options.filters] - Status values are OR-combined; opened/clicked false = sent but not opened/clicked;
 *   search matches a substring of the recipient email
 * @param {number} [options.page=1] - 1-based
 * @param {number} [options.pageSize=100] - Max 500
 * @returns {Promise<{sends: Array, filteredTotal: number, unsubscribedLeadIds: string[], page: number, pageSize: number}>}
 */
export const fetchCampaignSends = async (campaignId, { filters = {}, page = 1, pageSize = 100 } = {}) => {
	const params = new URLSearchParams({ ...filtersToQueryParams(filters), page, pageSize });
	return fetcher(`/api/v1/email/campaigns/${campaignId}/sends?${params}`);
};

/**
 * Fetches the campaign's recipients that were skipped because they are on the blacklist.
 * @param {string} campaignId - UUID of the campaign
 * @returns {Promise<{blockedRecipients: Array<{emailRecipient: string, companyName: string, crmContactId: string, crmCompanyId: string}>}>}
 */
export const fetchCampaignBlockedRecipients = async (campaignId) => {
	return fetcher(`/api/v1/email/campaigns/${campaignId}/blocked-recipients`);
};

/**
 * Re-schedules all failed and skipped sends of a campaign for another send attempt.
 * @param {string} campaignId - UUID of the campaign
 * @returns {Promise<{retried: number}>}
 */
export const retryFailedSends = async (campaignId) => {
	return fetcher(`/api/v1/email/campaigns/${campaignId}/retry-failed`, { method: 'POST' });
};

/**
 * Custom columns the campaign's steps use, with fallbacks and how many recipients each would skip.
 * @param {string} campaignId - UUID of the campaign
 * @returns {Promise<{variables: Array<{key: string, name: string, columnFallback: string|null, campaignFallback: string|null, required: boolean, missingCount: number}>, recipientCount: number, skippedCount: number}>}
 */
export const fetchCampaignCustomVariables = async (campaignId) => {
	return fetcher(`/api/v1/email/campaigns/${campaignId}/custom-variables`);
};

/**
 * Re-runs scheduling of an active campaign so recipients skipped for missing values get their sends.
 * @param {string} campaignId - UUID of the campaign
 * @returns {Promise<{status: string}>}
 */
export const rescheduleSkippedRecipients = async (campaignId) => {
	return fetcher(`/api/v1/email/campaigns/${campaignId}/reschedule-skipped`, { method: 'POST' });
};
